import { useEffect, useId, useRef, useState } from "react";
import { CardFace, GoldCard, ToolIcon } from "../features/cards/card-face";
import { PlayerAvatar } from "./player-avatar";
import "./rules-sheet.css";

const TOPICS = ["Overview", "Your turn", "Card effects", "Winning"] as const;
const TITLES = [
  "A shared mine. A secret role.",
  "On your turn, choose one.",
  "Know your cards.",
  "Three rounds. Most gold wins.",
];
const ACTIONS = [
  [
    "break-pickaxe",
    "Break",
    "Target: a player’s working tool",
    "Break a pickaxe, lantern, or cart. Any broken tool stops that player placing tunnels until all their tools are repaired.",
  ],
  [
    "repair-lantern-cart",
    "Repair",
    "Target: a matching broken tool",
    "Fix one tool, yours or someone else’s. A card showing two tools repairs only one of them—you choose.",
  ],
  [
    "map",
    "Map",
    "Target: a hidden destination",
    "Look at one destination privately. Only you see the result; the card stays hidden on the board.",
  ],
  [
    "rockfall",
    "Rockfall",
    "Target: a tunnel on the board",
    "Remove one tunnel, including a dead end. You cannot remove the entrance or a destination.",
  ],
] as const;

function PlacementExample({ valid }: { valid: boolean }) {
  return (
    <figure className="rules-placement">
      <div className="rules-placement__cards" aria-hidden="true">
        <CardFace faceId="start" />
        <div className={valid ? "rules-placement__preview" : "rules-placement__invalid"}>
          <CardFace faceId={valid ? "path-long-straight" : "path-short-straight"} />
        </div>
      </div>
      <figcaption>
        <strong>{valid ? "✓ Fits" : "× Doesn’t fit"}</strong>
        {valid
          ? "The tunnel connects to the entrance."
          : "A wall meets the entrance’s open tunnel."}
      </figcaption>
    </figure>
  );
}

function Overview() {
  return (
    <>
      <p className="rules-intro">
        One destination hides gold. Your secret role decides whether you help the crew reach it.
      </p>
      <figure className="rules-mine">
        <div className="rules-mine__entrance">
          <CardFace faceId="start" />
          <span>Entrance</span>
        </div>
        <div className="rules-mine__route" aria-hidden="true">
          <span>Build a path</span>
          <svg viewBox="0 0 120 24" fill="none">
            <path
              d="M0 12h114m-9-8 9 8-9 8"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="5 5"
            />
          </svg>
        </div>
        <div className="rules-mine__goals">
          <div aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <CardFace key={i} faceId="goal-back" />
            ))}
          </div>
          <span>3 hidden destinations</span>
        </div>
        <figcaption>Only one destination contains gold. The other two are rock.</figcaption>
      </figure>
      <div className="rules-roles">
        <article>
          <PlayerAvatar seat={4} />
          <div>
            <h3>Gold miner</h3>
            <p>Connect the entrance to the gold.</p>
          </div>
        </article>
        <article>
          <PlayerAvatar seat={9} />
          <div>
            <h3>Saboteur</h3>
            <p>Stop the crew reaching the gold.</p>
          </div>
        </article>
      </div>
      <p className="rules-note">
        Keep your role secret. Roles are dealt again each round, so your team may change.
      </p>
      <details className="rules-details">
        <summary>Playing with 2?</summary>
        <p>
          House rule: deal 2 of 3 roles—2 miners and 1 saboteur. The spare stays hidden, so there
          may be no saboteur.
        </p>
      </details>
    </>
  );
}

function YourTurn() {
  return (
    <>
      <p className="rules-intro">
        Use one card from your hand. Each of these choices takes your whole turn.
      </p>
      <div className="rules-choices">
        <article className="rules-choice">
          <figure className="rules-choice__example">
            <div className="rules-placement__cards" aria-hidden="true">
              <CardFace faceId="start" />
              <div className="rules-placement__preview">
                <CardFace faceId="path-long-straight" />
              </div>
            </div>
            <figcaption>Example: preview a highlighted space</figcaption>
            <span className="rules-example-control" aria-hidden="true">
              Place tunnel
            </span>
          </figure>
          <div>
            <h3>Build a tunnel</h3>
            <ol>
              <li>Select a tunnel card from your hand.</li>
              <li>Rotate it 180° if needed. Choose a highlighted space.</li>
              <li>
                Check the preview, then confirm with <strong>Place tunnel</strong>.
              </li>
            </ol>
          </div>
        </article>
        <article className="rules-choice">
          <figure className="rules-choice__example">
            <div className="rules-action-example" aria-hidden="true">
              <CardFace faceId="break-pickaxe" />
              <span>→</span>
              <ToolIcon tool="PICKAXE" broken />
            </div>
            <figcaption>Example: break another player’s pickaxe</figcaption>
            <span className="rules-example-control" aria-hidden="true">
              Break Maya’s pickaxe
            </span>
          </figure>
          <div>
            <h3>Play an action</h3>
            <ol>
              <li>Select an action card.</li>
              <li>Choose its target: a player and tool, a destination, or an existing tunnel.</li>
              <li>Review the action, then confirm.</li>
            </ol>
          </div>
        </article>
        <article className="rules-choice">
          <figure className="rules-choice__example">
            <div className="rules-discard-example" aria-hidden="true">
              <CardFace faceId="path-short-straight" />
              <span>→</span>
              <div className="rules-card-back">
                Discard
                <br />
                pile
              </div>
            </div>
            <figcaption>Example: discard a card you don’t want</figcaption>
            <span className="rules-example-control" aria-hidden="true">
              Discard card
            </span>
          </figure>
          <div>
            <h3>Discard a card</h3>
            <ol>
              <li>Select any card from your hand.</li>
              <li>
                Choose <strong>Discard card</strong>, then confirm.
              </li>
            </ol>
            <p>Discard it face down. You don’t need a board target, and this uses your turn.</p>
          </div>
        </article>
      </div>
      <p className="rules-note">
        <strong>Then the game takes over.</strong> You draw automatically if the deck has cards.
        Then the next player takes their turn, unless the round has ended. An empty deck means no
        replacement card.
      </p>
      <section className="rules-building">
        <h3>Can’t build?</h3>
        <p>
          Your tunnel must connect to the entrance through an unbroken path and match every
          neighbouring edge: tunnel to tunnel, wall to wall. You can rotate cards 180°, not
          sideways.
        </p>
        <div className="rules-placement-pair">
          <PlacementExample valid />
          <PlacementExample valid={false} />
        </div>
        <p>
          <strong>Broken tool?</strong> You cannot place tunnels until all your tools are repaired.
          You can still play actions or discard.
        </p>
      </section>
    </>
  );
}

function CardEffects() {
  return (
    <>
      <p className="rules-intro">
        Select a card to see its available targets. Every action uses one card and takes your turn.
      </p>
      <div className="rules-actions">
        {ACTIONS.map(([faceId, name, target, description]) => (
          <article key={name}>
            <CardFace faceId={faceId} />
            <div>
              <h3>{name}</h3>
              <p className="rules-target">{target}</p>
              <p>{description}</p>
            </div>
          </article>
        ))}
      </div>
      <p className="rules-note">
        <strong>No eligible target?</strong> Choose another card or discard. A broken tool stops
        building, but you can still use any action with an eligible target.
      </p>
    </>
  );
}

function Winning() {
  return (
    <>
      <div className="rules-gold-intro">
        <GoldCard value={3} />
        <p>
          Earn gold across <strong>3 rounds</strong>. Keep what you earn even when your role
          changes. The highest total wins the match; tied highest totals share the win.
        </p>
      </div>
      <section className="rules-outcome">
        <h3>Gold reached → miners win the round</h3>
        <p>
          A connected path from the entrance reaches the gold destination. Miners choose gold cards
          in turn; saboteurs earn nothing.
        </p>
      </section>
      <section className="rules-outcome">
        <h3>All cards used → saboteurs win the round</h3>
        <p>
          If gold has not been reached, play continues until the deck{" "}
          <strong>and every player’s hand</strong> are empty. Saboteurs receive gold; miners earn
          nothing.
        </p>
      </section>
      <p className="rules-note">
        No saboteurs dealt and no gold reached? Nobody earns gold. Other players’ totals stay
        private until the end of the match.
      </p>
      <details className="rules-details">
        <summary>Gold distribution details</summary>
        <h3>When miners win</h3>
        <p>
          The reward pool contains one gold card per player, up to 9 cards. Starting with the player
          who reached gold and moving counterclockwise, miners each choose one card; saboteurs are
          skipped. Repeat until the pool is empty.
        </p>
        <h3>When saboteurs win</h3>
        <table className="rules-payouts">
          <thead>
            <tr>
              <th scope="col">Saboteurs dealt</th>
              <th scope="col">Gold each</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">1</th>
              <td>4</td>
            </tr>
            <tr>
              <th scope="row">2 or 3</th>
              <td>3</td>
            </tr>
            <tr>
              <th scope="row">4</th>
              <td>2</td>
            </tr>
          </tbody>
        </table>
        <p>Example: 2 saboteurs receive 3 gold each.</p>
      </details>
    </>
  );
}

export function RulesSheet({ compact = false }: { compact?: boolean }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const launcher = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const contentId = useId();
  useEffect(() => {
    if (!open) return;
    const previous = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = previous;
    };
  }, [open]);
  const goTo = (next: number) => {
    setStep(next);
    body.current?.scrollTo({ top: 0 });
  };
  return (
    <>
      <button
        ref={launcher}
        type="button"
        className={compact ? "table-icon-button" : "rules-launcher"}
        aria-label="How to play"
        aria-haspopup="dialog"
        title="How to play"
        onClick={() => {
          goTo(0);
          setOpen(true);
          dialog.current?.showModal();
          closeButton.current?.focus();
        }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          aria-hidden="true"
        >
          {compact ? (
            <>
              <circle cx="12" cy="12" r="9" />
              <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 2-2.5 2-2.5 4" strokeLinecap="round" />
              <circle cx="12" cy="16.5" r=".9" fill="currentColor" stroke="none" />
            </>
          ) : (
            <>
              <path
                d="M12 5c-3-2-6-2-9-1v15c3-1 6-1 9 1 3-2 6-2 9-1V4c-3-1-6-1-9 1Zm0 0v15"
                strokeLinejoin="round"
              />
              <path d="m6 8 3 1m6 0 3-1" strokeLinecap="round" />
            </>
          )}
        </svg>
        {!compact ? (
          <>
            <span>How to play</span>
            <span aria-hidden="true">↗</span>
          </>
        ) : null}
      </button>
      <dialog
        ref={dialog}
        className="rules-guide"
        aria-labelledby={titleId}
        onKeyDown={(event) => {
          if (event.key === "Escape") event.stopPropagation();
        }}
        onClose={() => {
          setOpen(false);
          launcher.current?.focus();
        }}
      >
        <header className="rules-guide__header">
          <div>
            <span className="rules-guide__eyebrow">The field guide</span>
            <strong id={titleId}>How to play</strong>
          </div>
          <button
            ref={closeButton}
            type="button"
            className="rules-guide__close"
            aria-label="Close rules"
            onClick={() => dialog.current?.close()}
          >
            ×
          </button>
        </header>
        <div className="rules-guide__layout">
          <nav className="rules-guide__topics" aria-label="Rule topics">
            {TOPICS.map((topic, index) => (
              <button
                type="button"
                key={topic}
                id={`${contentId}-topic-${index}`}
                aria-current={step === index ? "true" : undefined}
                aria-controls={contentId}
                onClick={() => goTo(index)}
              >
                {topic}
                <span aria-hidden="true">↗</span>
              </button>
            ))}
          </nav>
          <div
            className="rules-guide__body"
            ref={body}
            id={contentId}
            role="region"
            aria-labelledby={`${contentId}-topic-${step}`}
            tabIndex={0}
          >
            <h2>{TITLES[step]}</h2>
            <div key={step}>
              {step === 0 ? (
                <Overview />
              ) : step === 1 ? (
                <YourTurn />
              ) : step === 2 ? (
                <CardEffects />
              ) : (
                <Winning />
              )}
            </div>
          </div>
        </div>
      </dialog>
    </>
  );
}
